import { Crossword } from "@/models/Cms";
import store, { VuexModuleNamespaces, DataStore } from '@/store';
import { getCrosswordByTitle } from "@/service/crosswords";
import { CrosswordSettings, Crossword as CrosswordTemplate, SpecialCell } from "@/models/Crossword";
import { timeSince, timestampToMMDDYYYY } from "./time";
import { createSeededHash, undoSeededHash } from "./crypto";

const LOCAL_ROOM_PREFERENCES = 'crosswordRoomPreferences';
const LOCAL_SETTINGS_STORAGE_KEY = 'crosswordSettings';
const LOCAL_CREATOR_STORAGE_KEY = 'crosswordCreator';
const LOCAL_CREATOR_GRID_STORAGE_KEY = 'crosswordCreatorGrid';
const LOCAL_UPLOAD_STORAGE_KEY = 'crosswordUpload';
const LOCAL_UPLOAD_GRID_STORAGE_KEY = 'crosswordUploadGrid';
const LOCAL_UPLOAD_TIME_STORAGE_KEY = 'crosswordUploadTime';
const PRIVATE_ROOM_NAMES: string[] = [
    "Burrow", "Nest", "Cave", "Ocean", "Grove", "Pond", "Forest", "Treehouse", "Meadow",
];

const PUBLIC_ROOM_NAMES: string[] = [
    "Woodland", "Fjord", "Shore", "Island", "Bay", "Bluff", "Harbor", "Canyon", "Field",
    "Plateau", "Foothill", "Bayou", "Rainforest", "Sanctuary", "Crag", "Peninsula", "Tundra",
    "Thicket", "Waterfall", "Riverbank", "Cliffside", "Brook", "Marsh", "Creek", "Ravine",
    "Den", "Glade", "Hollow", "Lagoon", "Oasis", "Valley", "Hilltop", "Prairie", "Glen",
];

const DEFAULT_ROOM_ADJECTIVES: string[] = [
    "Witty", "Silly", "Bouncy", "Curious", "Playful", "Fluffy", "Zany", "Cheerful", "Dashing",
    "Snoozy", "Jolly", "Perky", "Frolicsome", "Mischievous", "Whimsical", "Quirky", "Chipper",
    "Jovial", "Lively", "Sprightly", "Peppy", "Jaunty", "Gleeful", "Breezy", "Gallant",
];

const COMMUNITY_ROOM_ADJECTIVES: string[] = [
    "Dapper", "Bubbly", "Giggly", "Spunky", "Snazzy", "Huggable", "Chirpy", "Ebullient",
    "Spirited", "Zippy", "Frisky", "Twinkly", "Jumpy", "Snappy", "Dandy", "Merry",
    "Frivolous", "Peppy", "Brisk", "Brilliant", "Swanky", "Jazzy", "Vibrant", "Spiffy",
];

export enum PUZZLE_MODES {
    AI_PUZZLES = "default",
    COMMUNITY_PUZZLES = "community"
  }

export async function queryPuzzleByTitle(title: string): Promise<Crossword | undefined> {
    // Check cache
    const postCache = store.getters[`${VuexModuleNamespaces.App}/${DataStore.getters.getPostCache.name}`];
    if (postCache.has(title)) {
        return postCache.get(title);
    } else {
        // Fetch content and update cache
        const posts = await (await getCrosswordByTitle(title)).data.data
        store.commit(`${VuexModuleNamespaces.App}/${DataStore.mutations.updatePostCache.name}`, posts);
        if (posts.length > 0) {
            return posts[0];
        }
    }
}

export function loadRoomPuzzlePreferencesFromLocalStorage(): PUZZLE_MODES {
    const storedSettings = localStorage.getItem(LOCAL_ROOM_PREFERENCES);
    if (storedSettings) {
        return storedSettings as PUZZLE_MODES; // Casting works for string enums
    } else {
        return PUZZLE_MODES.AI_PUZZLES;
    }
}

export function saveRoomPuzzlePreferencesInLocalStorage(update: PUZZLE_MODES): void {
    try {
        localStorage.setItem(LOCAL_ROOM_PREFERENCES, update); // Save directly as a string value
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
}

export function loadSettingsFromLocalStorage(): CrosswordSettings {
    const storedSettings = localStorage.getItem(LOCAL_SETTINGS_STORAGE_KEY);

    if (storedSettings) {
        try {
            return JSON.parse(storedSettings) as CrosswordSettings;
        } catch (error) {
            console.error('Error parsing settings from localStorage:', error);
        }
    }

    // Return default settings if none are found in local storage
    return {
        skipFilledLetters: true,
        moveToNextClueAtEndOfWord: true,
        spaceToggleDirection: true,
    };
}

export function loadCreatorFromLocalStorage(): CrosswordTemplate | null {
   const storedPuzzle = localStorage.getItem(LOCAL_CREATOR_STORAGE_KEY);
   if (storedPuzzle) {
      try {
          return JSON.parse(storedPuzzle) as CrosswordTemplate;
      } catch (error) {
          console.error('Error getting from localStorage:', error);
      }
  }
  return null;
}

export function saveCreatorProgressInLocalStorage(update: CrosswordTemplate | null): void {
   try {
      localStorage.setItem(LOCAL_CREATOR_STORAGE_KEY, JSON.stringify(update));
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
}

export function loadUploadTimeFromLocalStorage(): string | null {
    const storedPuzzle = localStorage.getItem(LOCAL_UPLOAD_TIME_STORAGE_KEY);
    if (storedPuzzle) {
        try {
            return storedPuzzle;
        } catch (error) {
            console.error('Error getting from localStorage:', error);
        }
    }
    return null;
}
 
export function saveUploadTimeProgressInLocalStorage(update: string): void {
    try {
        localStorage.setItem(LOCAL_UPLOAD_TIME_STORAGE_KEY, update);
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
}


export function loadUploadFromLocalStorage(): CrosswordTemplate | null {
    const storedPuzzle = localStorage.getItem(LOCAL_UPLOAD_STORAGE_KEY);
    if (storedPuzzle) {
        try {
            return JSON.parse(storedPuzzle) as CrosswordTemplate;
        } catch (error) {
            console.error('Error getting from localStorage:', error);
        }
    }
    return null;
}
 
export function saveUploadProgressInLocalStorage(update: CrosswordTemplate | null): void {
    try {
        localStorage.setItem(LOCAL_UPLOAD_STORAGE_KEY, JSON.stringify(update));
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
}

export function loadUploadGridFromLocalStorage(): string[][] | null {
    const storedGrid = localStorage.getItem(LOCAL_UPLOAD_GRID_STORAGE_KEY);
    if (storedGrid) {
        try {
            return JSON.parse(storedGrid) as string[][];
        } catch (error) {
            console.error('Error parsing grid from localStorage:', error);
        }
    }
    return null;
}
 
export function saveUploadGridProgressInLocalStorage(update: string[][] | null): void {
    try {
        localStorage.setItem(LOCAL_UPLOAD_GRID_STORAGE_KEY, JSON.stringify(update));
    } catch (error) {
        console.error('Error saving grid to localStorage:', error);
    }
}

export function loadCreatorGridFromLocalStorage(): string[][] | null {
   const storedGrid = localStorage.getItem(LOCAL_CREATOR_GRID_STORAGE_KEY);
   if (storedGrid) {
      try {
          return JSON.parse(storedGrid) as string[][];
      } catch (error) {
          console.error('Error parsing grid from localStorage:', error);
      }
  }
  return null;
}

export function saveCreatorGridProgressInLocalStorage(update: string[][] | null): void {
   try {
      localStorage.setItem(LOCAL_CREATOR_GRID_STORAGE_KEY, JSON.stringify(update));
  } catch (error) {
      console.error('Error saving grid to localStorage:', error);
  }
}

export function updateSettingsInLocalStorage(update: CrosswordSettings): void {
    try {
        localStorage.setItem(LOCAL_SETTINGS_STORAGE_KEY, JSON.stringify(update));
        emitCrosswordSettingsUpdateEvent();
    } catch (error) {
        console.error('Error saving settings to localStorage:', error);
    }
}

export function emitCrosswordSettingsUpdateEvent(): void {
    const event = new Event('xframe:refreshCrosswordSettings', {
      cancelable: true
    });
    window.dispatchEvent(event);
}

export function getTitleFromTags(puzzle: CrosswordTemplate): string | null {
   try {
      const createdTitle = puzzle.tags.find((tag) => tag.startsWith('Title:'));
      if (createdTitle) {
          return createdTitle.split(':')[1];
      }
      return null;
  } catch (err) {
      // Ignore
  }
  return null;
}

export function getUserIdFromTags(puzzle: CrosswordTemplate): string | null {
   try {
      const userId = puzzle.tags.find((tag) => tag.startsWith('UserId:'));
      if (userId) {
          return userId.split(':')[1];
      }
      return null;
  } catch (err) {
      // Ignore
  }
  return null;
}

export function getUsernameFromTags(puzzle: CrosswordTemplate): string | null {
   try {
      const userId = puzzle.tags.find((tag) => tag.startsWith('Username:'));
      if (userId) {
          return userId.split(':')[1];
      }
      return null;
  } catch (err) {
      // Ignore
  }
  return null;
}

export function getCopyrightFromTags(puzzle: CrosswordTemplate): string | null {
    try {
       const userId = puzzle.tags.find((tag) => tag.startsWith('Copyright:'));
       if (userId) {
           return userId.split(':')[1];
       }
       return null;
   } catch (err) {
       // Ignore
   }
   return null;
 }

 export function getPublisherFromTags(puzzle: CrosswordTemplate): string | null {
    try {
       const userId = puzzle.tags.find((tag) => tag.startsWith('Publisher:'));
       if (userId) {
           return userId.split(':')[1];
       }
       return null;
   } catch (err) {
       // Ignore
   }
   return null;
 }

export function getCreationDateFromTags(puzzle: CrosswordTemplate): string {
   const createdTag = puzzle.tags.find((tag) => tag.startsWith('Created:'));
   if (createdTag) {
       const epochSeconds = createdTag.split(':')[1];
       return timeSince((Number(epochSeconds)));
   }
   return timeSince(757382400);
}

export function getCreationAbsoluteDateFromTags(puzzle: CrosswordTemplate): string {
   const createdTag = puzzle.tags.find((tag) => tag.startsWith('Created:'));
   if (createdTag) {
       const epochSeconds = createdTag.split(':')[1];
       return timestampToMMDDYYYY((Number(epochSeconds)));
   }
   return timestampToMMDDYYYY(757382400);
}

export function generateLobbyName(roomMode: "default" | "community" = "default", isPrivateMode = false): string {
    const adjectives = roomMode === "community" ? COMMUNITY_ROOM_ADJECTIVES : DEFAULT_ROOM_ADJECTIVES;
    const locations = isPrivateMode ? PRIVATE_ROOM_NAMES : PUBLIC_ROOM_NAMES;
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const location = locations[Math.floor(Math.random() * locations.length)];
    const uniqueId = Math.random().toString(36).substring(2, 8);
    return `${adjective} ${location} ${uniqueId}`;
}

export function derivePuzzleSolution(crossword: CrosswordTemplate): string[][] {
    const { acrossClues, downClues } = crossword;
    const solutionGrid: string[][] = Array.from(Array(crossword.y), () =>
        Array(crossword.x).fill('')
    );
    for (const clue of acrossClues) {
      const undoneHash = undoSeededHash(clue.solution, crossword.seed);
      const start = clue.x;
      for (const [index, letter] of [...undoneHash].entries()) {
        solutionGrid[start + index][clue.y] = letter;
      }
    }
    for (const clue of downClues) {
      const undoneHash = undoSeededHash(clue.solution, crossword.seed);
      const start = clue.y;
      for (const [index, letter] of [...undoneHash].entries()) {
        solutionGrid[clue.x][start + index] = letter;
      }
    }
    return solutionGrid;
}

export function deriveIPuzSolution(crossword: CrosswordTemplate): string[][] {
    const { acrossClues, downClues } = crossword;
    const solutionGrid: string[][] = Array.from(Array(crossword.y), () =>
        Array(crossword.x).fill('#')
    );
    for (const clue of acrossClues) {
      const undoneHash = undoSeededHash(clue.solution, crossword.seed);
      const start = clue.x;
      for (const [index, letter] of [...undoneHash].entries()) {
        solutionGrid[clue.y][start + index] = letter;
      }
    }
    for (const clue of downClues) {
      const undoneHash = undoSeededHash(clue.solution, crossword.seed);
      const start = clue.y;
      for (const [index, letter] of [...undoneHash].entries()) {
        solutionGrid[start + index][clue.x] = letter;
      }
    }
    return solutionGrid;
}

export function deriveIPuzGridSyntax(crossword: CrosswordTemplate): string[][] {
    const solutionGrid = Array.from(Array(crossword.y), () =>
        Array(crossword.x).fill(0)
    );

    // Iterate over each cell and determine if a black cell, clue cell, or empty
    for (let i = 0; i < crossword.y; i++) {
        for (let j = 0; j < crossword.x; j++) {
            // Check if black cell
            const isBlackCell = crossword.blackCells.some(blackCell => blackCell.x === j && blackCell.y === i);
            if (isBlackCell) {
                solutionGrid[i][j] = "#";
            }
    
            // Check if down clue
            const downClue = crossword.downClues.find(clue => clue.x === j && clue.y === i);
            if (downClue) {
                solutionGrid[i][j] = downClue.number;
            }
    
            // Check if across clue
            const acrossClue = crossword.acrossClues.find(clue => clue.x === j && clue.y === i);
            if (acrossClue) {
                solutionGrid[i][j] = acrossClue.number;
            }
        }
    }

    return solutionGrid;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertCluesFormat(data: any): any {
    if (data.clues) {
        ["Across", "Down"].forEach((direction) => {
            if (
                Array.isArray(data.clues[direction]) &&
                data.clues[direction].some(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (item: any) => typeof item === 'object' && 'number' in item && 'clue' in item
                )
            ) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data.clues[direction] = data.clues[direction].map((item: any) => [item.number, item.clue]);
            }
        });
    }
    if (data.solution) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data.solution.forEach((row: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            row.forEach((col: any, index: number) => {
                if (typeof col === 'object') {
                    row[index] = col.value; 
                }
            });
        });
    }
    return data;
}

export function convertIPuzToCrossword(ipuzJson: string): CrosswordTemplate {
    let crosswordJson = JSON.parse(ipuzJson);
    const seed = "cinnabun";

    // Pre-convert clue formats
    crosswordJson = convertCluesFormat(crosswordJson);

    // Build puzzle
    const specialCells: SpecialCell[] = []
    for (let row = 0; row < crosswordJson.puzzle.length; row++) {
        for (let col = 0; col < crosswordJson.puzzle[row].length; col++) {
            if (crosswordJson.puzzle[row][col].style) {
                specialCells.push({
                    "x": col,
                    "y": row,
                    "circle": crosswordJson.puzzle[row][col].style.shapebg === "circle" ? true : false,
                    "highlight": crosswordJson.puzzle[row][col].style.highlight ? true : false,
                    "value": crosswordJson.puzzle[row][col].value
                })
            }
        }
    }
    const acrossClues = crosswordJson.clues.Across.map((clue: [number, string]) => {
        let x = -1;
        let y = -1;
        let solution = '';
    
        // Find the position of the clue on the grid
        for (let row = 0; row < crosswordJson.puzzle.length; row++) {
            for (let col = 0; col < crosswordJson.puzzle[row].length; col++) {
                if (crosswordJson.puzzle[row][col] === clue[0] ||
                    crosswordJson.puzzle[row][col].cell === clue[0]
                ) {
                    x = col;
                    y = row;
                    break;
                }
            }
            if (x !== -1 && y !== -1) break;
        }
    
        // Get the solution for this clue by matching its x and y position
        for (let i = 0; i < crosswordJson.solution.length; i++) { // Iterate over rows
            for (let j = 0; j < crosswordJson.solution[i].length; j++) { // Iterate over columns
                if (crosswordJson.puzzle[i][j] === clue[0] ||
                    crosswordJson.puzzle[i][j].cell === clue[0]
                ) { // Check if the clue number matches
                    let k = j;
                    while (k < crosswordJson.solution[i].length) { // Move rightward
                        if (crosswordJson.solution[i][k] === "#") {
                            break; // Stop at black squares
                        }
                        solution += crosswordJson.solution[i][k];
                        k++;
                    }
                }
            }
        }
    
        return {
            id: `across-${clue[0]}`,
            number: clue[0],
            clue: clue[1].replaceAll('&quot;', '"'),
            length: solution.length,
            x: x,
            y: y,
            solution: createSeededHash(seed, solution),
        };
    });
    const downClues = crosswordJson.clues.Down.map((clue: [number, string]) => {
        let x = -1;
        let y = -1;
        let solution = '';
    
        // Find the position of the clue on the grid
        for (let row = 0; row < crosswordJson.puzzle.length; row++) {
            for (let col = 0; col < crosswordJson.puzzle[row].length; col++) {
                if (crosswordJson.puzzle[row][col] === clue[0] ||
                    crosswordJson.puzzle[row][col].cell === clue[0]
                ) {
                    x = col;
                    y = row;
                    break;
                }
            }
            if (x !== -1 && y !== -1) break;
        }
    
        // Get the solution for this clue by matching its x and y position
        for (let j = 0; j < crosswordJson.solution[0].length; j++) { // Iterate over columns
            for (let i = 0; i < crosswordJson.solution.length; i++) { // Iterate over rows
                if (crosswordJson.puzzle[i][j] === clue[0] ||
                    crosswordJson.puzzle[i][j].cell === clue[0]
                ) { // Check if the clue number matches
                    let k = i;
                    while (k < crosswordJson.solution.length) { // Move downward
                        if (crosswordJson.solution[k][j] === "#") {
                            break; // Stop at black squares
                        }
                        solution += crosswordJson.solution[k][j];
                        k++;
                    }
                }
            }
        }
        return {
            id: `down-${clue[0]}`,
            number: clue[0],
            clue: clue[1].replaceAll('&quot;', '"'),
            length: solution.length,
            x: x,
            y: y,
            solution: createSeededHash(seed, solution),
        };
    });
    const blackCells = crosswordJson.puzzle.flatMap((row: string[], rowIndex: number) =>
        row.map((cell, colIndex) => {
            if (cell === "#") {
                return {
                    x: colIndex,
                    y: rowIndex,
                };
            }
            return null;
        }).filter(cell => cell !== null) // Filter out nulls
    );
    
    const crossword: CrosswordTemplate = {
        puzzleId:`crosswordrace-${Math.random().toString(36).substr(2, 4)}`,
        tags: [
            crosswordJson.title && `Title:${crosswordJson.title}`,
            crosswordJson.author && `Username:${crosswordJson.author}`,
            crosswordJson.publisher && `Publisher:${crosswordJson.publisher}`,
            crosswordJson.copyright && `Copyright:${crosswordJson.copyright}`
        ].filter(Boolean),
        x: crosswordJson.dimensions.width,
        y: crosswordJson.dimensions.height,
        seed: seed,
        blackCells: blackCells,
        acrossClues: acrossClues,
        downClues: downClues,
        specialCells: specialCells
    };
    console.log(crossword);
    return crossword;
}

export function downloadToIPuzJson(crossword: CrosswordTemplate, username: string | undefined): void {
    const content = JSON.stringify({
        "origin": "crosswordrace.com",
        "version": "http://ipuz.org/v1",
        "kind": [
            "http://ipuz.org/crossword#1"
        ],
        "author": username,
        "publisher": "crosswordrace.com",
        "title": getTitleFromTags(crossword),
        "empty": "0",
        "dimensions": {
            "width": crossword.x,
            "height": crossword.y
        },
        "puzzle": deriveIPuzGridSyntax(crossword),
        "clues": {
            "Across": crossword.acrossClues.map((clue) => [clue.number, clue.clue]),
            "Down": crossword.downClues.map((clue) => [clue.number, clue.clue]),
        },
        "solution": deriveIPuzSolution(crossword),
    }, null, 2); 

    // Download
    const blob = new Blob([content], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `crosswordrace-${crossword.puzzleId.slice(0, 4)}.ipuz`;
    link.style.display = 'none';
    document.body.appendChild(link);
    requestAnimationFrame(() => {
        link.click();
        document.body.removeChild(link);
    });
}

export const BLANK_TEMPLATES: CrosswordTemplate[] = [
    {
        "puzzleId": "5x5 Empty Grid",
        "tags": [],
        "x": 5,
        "y": 5,
        "seed": "Cinnabun",
        "blackCells": [],
        "acrossClues":[
            {
               "id":"fcd2a99b-d6a5-4449-a327-fa0a123deeeb",
               "number":1,
               "clue":"",
               "length":5,
               "x":0,
               "y":0,
               "solution":""
            },
            {
               "id":"ea485a85-55c9-4ec8-874e-9658ad213e85",
               "number":6,
               "clue":"",
               "length":5,
               "x":0,
               "y":1,
               "solution":""
            },
            {
               "id":"6ed207bd-cf78-4bcb-8c70-6893a85f4bcf",
               "number":7,
               "clue":"",
               "length":5,
               "x":0,
               "y":2,
               "solution":""
            },
            {
               "id":"de9ba9e6-c614-4fa8-866f-94d784b79013",
               "number":8,
               "clue":"",
               "length":5,
               "x":0,
               "y":3,
               "solution":""
            },
            {
               "id":"22f68162-284c-4a5f-ad4e-d89624abb4ac",
               "number":9,
               "clue":"",
               "length":5,
               "x":0,
               "y":4,
               "solution":""
            }
         ],
         "downClues":[
            {
               "id":"57bfd6f9-3f28-46cc-9d38-cb21106348e5",
               "number":1,
               "clue":"",
               "length":5,
               "x":0,
               "y":0,
               "solution":""
            },
            {
               "id":"38465792-4c22-48e0-87cf-899afa81e145",
               "number":2,
               "clue":"",
               "length":5,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"0c4979fb-e710-4372-b33c-200ae232c210",
               "number":3,
               "clue":"",
               "length":5,
               "x":2,
               "y":0,
               "solution":""
            },
            {
               "id":"e05cf040-6e54-4a20-abbd-1b6a3c6f5135",
               "number":4,
               "clue":"",
               "length":5,
               "x":3,
               "y":0,
               "solution":""
            },
            {
               "id":"8e84ba9c-9046-4bf5-8f4e-839e521de86a",
               "number":5,
               "clue":"",
               "length":5,
               "x":4,
               "y":0,
               "solution":""
            }
         ]
    },
    {
        "puzzleId": "5x5 Variant 1",
        "tags": [],
        "x": 5,
        "y": 5,
        "seed": "Cinnabun",
        "blackCells": [
            {
                "x": 0,
                "y": 0
            },
            {
                "x": 0,
                "y": 1
            },
            {
                "x": 4,
                "y": 3
            },
            {
                "x": 4,
                "y": 4
            }
        ],
        "acrossClues":[
            {
               "id":"7efa3004-0603-48ac-9638-5c0223bc1eb8",
               "number":1,
               "clue":"",
               "length":4,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"e66e3402-b528-45a0-8aa7-2c08505a4991",
               "number":5,
               "clue":"",
               "length":4,
               "x":1,
               "y":1,
               "solution":""
            },
            {
               "id":"b344c6d7-6253-47f9-a53c-8c7416c75501",
               "number":6,
               "clue":"",
               "length":5,
               "x":0,
               "y":2,
               "solution":""
            },
            {
               "id":"227186cf-e974-4d6e-a0d6-2a03d512383f",
               "number":7,
               "clue":"",
               "length":4,
               "x":0,
               "y":3,
               "solution":""
            },
            {
               "id":"ee0e8acf-42b8-461a-8113-89a1501d887b",
               "number":8,
               "clue":"",
               "length":4,
               "x":0,
               "y":4,
               "solution":""
            }
         ],
         "downClues":[
            {
               "id":"e9e37604-bb11-475a-938e-991541e4adf6",
               "number":1,
               "clue":"",
               "length":5,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"69334ac3-c2c2-4de3-b089-443385a93f49",
               "number":2,
               "clue":"",
               "length":5,
               "x":2,
               "y":0,
               "solution":""
            },
            {
               "id":"3e50ed7b-c902-4061-b224-6589fa843172",
               "number":3,
               "clue":"",
               "length":5,
               "x":3,
               "y":0,
               "solution":""
            },
            {
               "id":"9140ba08-0b44-44f1-bfca-f33638ebe42b",
               "number":4,
               "clue":"",
               "length":3,
               "x":4,
               "y":0,
               "solution":""
            },
            {
               "id":"6d4c9e16-4f17-422d-8867-d1e5efc96169",
               "number":6,
               "clue":"",
               "length":3,
               "x":0,
               "y":2,
               "solution":""
            }
        ]
    },
    {
        "puzzleId": "5x5 Variant 2",
        "tags": [],
        "x": 5,
        "y": 5,
        "seed": "Cinnabun",
        "blackCells": [
            {
                "x": 4,
                "y": 0
            },
            {
                "x": 4,
                "y": 1
            },
            {
                "x": 0,
                "y": 3
            },
            {
                "x": 0,
                "y": 4
            }
        ],
        "acrossClues":[
            {
               "id":"11623c28-310e-4391-8094-369cc8756c12",
               "number":1,
               "clue":"",
               "length":4,
               "x":0,
               "y":0,
               "solution":""
            },
            {
               "id":"06ba0f58-9fa7-4f8c-91ab-b317692aa189",
               "number":5,
               "clue":"",
               "length":4,
               "x":0,
               "y":1,
               "solution":""
            },
            {
               "id":"bab1c80b-f96a-499b-b3ca-b38acc3bfe43",
               "number":6,
               "clue":"",
               "length":5,
               "x":0,
               "y":2,
               "solution":""
            },
            {
               "id":"c7034146-70d9-43bb-bc48-0c2a8ad878b9",
               "number":8,
               "clue":"",
               "length":4,
               "x":1,
               "y":3,
               "solution":""
            },
            {
               "id":"da2407a1-6c49-4d51-8e10-f518f11b9181",
               "number":9,
               "clue":"",
               "length":4,
               "x":1,
               "y":4,
               "solution":""
            }
         ],
         "downClues":[
            {
               "id":"3e15b896-569c-49eb-9b3c-e30f8df317a3",
               "number":1,
               "clue":"",
               "length":3,
               "x":0,
               "y":0,
               "solution":""
            },
            {
               "id":"a36fb1e2-6491-42c0-8d2a-da8c9ad5c4c3",
               "number":2,
               "clue":"",
               "length":5,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"262e58ec-a0bb-4376-a3c0-bcc1f6d81567",
               "number":3,
               "clue":"",
               "length":5,
               "x":2,
               "y":0,
               "solution":""
            },
            {
               "id":"09734b76-e127-4ef8-9d9e-c5990258bbd2",
               "number":4,
               "clue":"",
               "length":5,
               "x":3,
               "y":0,
               "solution":""
            },
            {
               "id":"8a0fe9b9-04ce-49a0-90c0-7504ad300713",
               "number":7,
               "clue":"",
               "length":3,
               "x":4,
               "y":2,
               "solution":""
            }
         ]
    },
    {
        "puzzleId": "5x5 Variant 3",
        "tags": [],
        "x": 5,
        "y": 5,
        "seed": "Cinnabun",
        "blackCells": [
            {
                "x": 4,
                "y": 0
            },
            {
                "x": 0,
                "y": 4
            }
        ],
        "acrossClues":[
            {
               "id":"59a054f2-c4d2-4d33-8978-e0d8d2a30b4b",
               "number":1,
               "clue":"",
               "length":4,
               "x":0,
               "y":0,
               "solution":""
            },
            {
               "id":"d23a0880-4a12-4c70-98ac-9ee29bf30ec2",
               "number":5,
               "clue":"",
               "length":5,
               "x":0,
               "y":1,
               "solution":""
            },
            {
               "id":"22e6bd10-3266-4b7d-9499-6d83e8bad1fd",
               "number":7,
               "clue":"",
               "length":5,
               "x":0,
               "y":2,
               "solution":""
            },
            {
               "id":"87dcbdd8-bf36-45a7-8b0c-0fd0fce27bfb",
               "number":8,
               "clue":"",
               "length":5,
               "x":0,
               "y":3,
               "solution":""
            },
            {
               "id":"e4fa744d-0d6e-4a9f-a8e6-db70b296fd15",
               "number":9,
               "clue":"",
               "length":4,
               "x":1,
               "y":4,
               "solution":""
            }
         ],
         "downClues":[
            {
               "id":"a2fbd65e-5e31-4ce0-8305-35bc2338e249",
               "number":1,
               "clue":"",
               "length":4,
               "x":0,
               "y":0,
               "solution":""
            },
            {
               "id":"41533d8f-e6a7-4144-b8de-ded5e3e84c33",
               "number":2,
               "clue":"",
               "length":5,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"cea494af-8d64-4447-a336-9d3db2041fef",
               "number":3,
               "clue":"",
               "length":5,
               "x":2,
               "y":0,
               "solution":""
            },
            {
               "id":"ae99bd9b-1a99-423c-9d0f-b2451d510e27",
               "number":4,
               "clue":"",
               "length":5,
               "x":3,
               "y":0,
               "solution":""
            },
            {
               "id":"e8d05312-c1ef-48d0-b872-dec55701397d",
               "number":6,
               "clue":"",
               "length":4,
               "x":4,
               "y":1,
               "solution":""
            }
         ]
    },
    {
        "puzzleId": "5x5 Variant 4",
        "tags": [],
        "x": 5,
        "y": 5,
        "seed": "Cinnabun",
        "blackCells": [
            {
                "x": 0,
                "y": 0
            },
            {
                "x": 4,
                "y": 4
            }
        ],
        "acrossClues":[
            {
               "id":"154c988c-1b38-45d0-b436-b40778d21cd6",
               "number":1,
               "clue":"",
               "length":4,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"c3bdcb8c-6062-4092-826c-ce28cf609c4e",
               "number":5,
               "clue":"",
               "length":5,
               "x":0,
               "y":1,
               "solution":""
            },
            {
               "id":"08dc1350-7c1d-4960-8e19-f50abbcc8fbf",
               "number":6,
               "clue":"",
               "length":5,
               "x":0,
               "y":2,
               "solution":""
            },
            {
               "id":"e1093d86-d45e-4972-8419-ec387aa49d19",
               "number":7,
               "clue":"",
               "length":5,
               "x":0,
               "y":3,
               "solution":""
            },
            {
               "id":"bd31a3ff-e0ef-490e-9e83-94e608b69da9",
               "number":8,
               "clue":"",
               "length":4,
               "x":0,
               "y":4,
               "solution":""
            }
         ],
         "downClues":[
            {
               "id":"92b4087c-bb58-4a68-afc0-f2d28c3b732a",
               "number":1,
               "clue":"",
               "length":5,
               "x":1,
               "y":0,
               "solution":""
            },
            {
               "id":"d2866a36-fdec-4677-9478-de9946951118",
               "number":2,
               "clue":"",
               "length":5,
               "x":2,
               "y":0,
               "solution":""
            },
            {
               "id":"0e98c866-246e-47e9-bbdc-9caa680f0bde",
               "number":3,
               "clue":"",
               "length":5,
               "x":3,
               "y":0,
               "solution":""
            },
            {
               "id":"2a818ac2-8b64-4a0d-a14f-9b44fdf9547e",
               "number":4,
               "clue":"",
               "length":4,
               "x":4,
               "y":0,
               "solution":""
            },
            {
               "id":"f3d06e70-c580-4501-8c68-529fe75bf546",
               "number":5,
               "clue":"",
               "length":4,
               "x":0,
               "y":1,
               "solution":""
            }
         ]
    }
]

export const SAMPLE_MINI: CrosswordTemplate = {
   "puzzleId": "SAMPLE_MINI",
   "tags": [],
   "x": 5,
   "y": 5,
   "seed": "Cinnabun",
   "blackCells": [
       {
           "x": 0,
           "y": 0
       },
       {
           "x": 0,
           "y": 1
       },
       {
           "x": 4,
           "y": 3
       },
       {
           "x": 4,
           "y": 4
       }
   ],
   "acrossClues": [
       {
           "id": "7b2861c6-4fd8-4960-ab2c-27515dc53e87",
           "number": 1,
           "clue": "Film director's nickname",
           "length": 4,
           "x": 1,
           "y": 0,
           "solution": "HnssfgzsFQFS"
       },
       {
           "id": "f51bf682-1680-489d-bda7-78134c709214",
           "number": 5,
           "clue": "Final resting place of ancient secrets",
           "length": 4,
           "x": 1,
           "y": 1,
           "solution": "HnssfgzsYTRG"
       },
       {
           "id": "634251de-b3da-449d-8bb2-024944c169dc",
           "number": 6,
           "clue": "Arcane art of persuasion",
           "length": 5,
           "x": 0,
           "y": 2,
           "solution": "HnssfgzsRFLNH"
       },
       {
           "id": "d23a10db-fd8c-41fc-b73c-fdeebd616c89",
           "number": 7,
           "clue": "Ireland's poetic muse",
           "length": 4,
           "x": 0,
           "y": 3,
           "solution": "HnssfgzsJWNS"
       },
       {
           "id": "4ddd8544-57f9-429c-82a0-15e35db908a3",
           "number": 8,
           "clue": "Federal case file prefix",
           "length": 4,
           "x": 0,
           "y": 4,
           "solution": "HnssfgzsWNHT"
       }
   ],
   "downClues": [
       {
           "id": "c3828a46-b4c9-41fc-bc2c-ffdb17f52ea0",
           "number": 1,
           "clue": "Early console pioneer",
           "length": 5,
           "x": 1,
           "y": 0,
           "solution": "HnssfgzsFYFWN"
       },
       {
           "id": "d7787542-32a2-4f5a-b2f9-fcd6703636e6",
           "number": 2,
           "clue": "Method in madness?",
           "length": 5,
           "x": 2,
           "y": 0,
           "solution": "HnssfgzsQTLNH"
       },
       {
           "id": "2bda3a9b-9ea6-4b2a-9fb4-9ba808d33162",
           "number": 3,
           "clue": "Protein prefix in biochemistry codes",
           "length": 5,
           "x": 3,
           "y": 0,
           "solution": "HnssfgzsFRNST"
       },
       {
           "id": "a1dc9164-97fd-4dd2-bdd0-58b23fe2c319",
           "number": 4,
           "clue": "Network's initials in every home?",
           "length": 3,
           "x": 4,
           "y": 0,
           "solution": "HnssfgzsSGH"
       },
       {
           "id": "e0b619a4-0040-4d11-b956-35769861ad14",
           "number": 6,
           "clue": "Crew's concern in naval warfare",
           "length": 3,
           "x": 0,
           "y": 2,
           "solution": "HnssfgzsRJW"
       }
   ]
}
